/*
  Purchase options (one-time / subscription) rendered by snippets/selling-plans.liquid.

  The element keeps a hidden input[name="selling_plan"] in step with the chosen radio or plan
  select; product-form.js reads it through window.getCurrentSellingPlanId(). An empty value means
  a one-time purchase. On variant change the options are swapped for the ones rendered for the new
  variant, and the previous choice is kept when that plan still exists.
*/
class ProductSubscriptions extends HTMLElement {
  connectedCallback() {
    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);
    this.sync();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    if (this.unsubscribeVariant) this.unsubscribeVariant();
    this.unsubscribeVariant = undefined;
  }

  get input() {
    return this.querySelector('input[name="selling_plan"]');
  }

  rowOf(control) {
    let node = control;
    while (node && node.parentElement && node.parentElement !== this) {
      if (node.querySelector && node.querySelector('input[name="purchase_option"]')) {
        const radios = node.querySelectorAll('input[name="purchase_option"]');
        if (radios.length === 1) return node;
      }
      node = node.parentElement;
    }
    return control.parentElement;
  }

  setPlan(value) {
    const input = this.input;
    if (input) input.value = !value || value === 'onetime' ? '' : value;
  }

  onChange(event) {
    const target = event.target;
    if (!target || !target.name) return;

    if (target.name === 'purchase_option') {
      // a subscription radio: keep the plan select in its row on the same plan
      const select = this.rowOf(target).querySelector('select[name="purchase_option_values"]');
      if (select && target.value !== 'onetime') {
        const match = Array.from(select.options).find((option) => option.value === target.value);
        if (match) select.value = target.value;
      }
      this.setPlan(target.value);
    } else if (target.name === 'purchase_option_values') {
      const radio = this.rowOf(target).querySelector('input[name="purchase_option"]');
      if (radio) radio.checked = true;
      this.setPlan(target.value);
    }
  }

  // Make the hidden input reflect whatever is checked in the markup.
  sync() {
    const checked = this.querySelector('input[name="purchase_option"]:checked');
    if (!checked) return this.setPlan('');
    const select = this.rowOf(checked).querySelector('select[name="purchase_option_values"]');
    const viaSelect =
      checked.value !== 'onetime' && select && Array.from(select.options).some((option) => option.value === checked.value);
    this.setPlan(viaSelect ? select.value : checked.value);
  }

  onVariantChange(event) {
    const data = event && event.data;
    if (!data || !data.html || typeof data.html.querySelector !== 'function') return;

    // ignore variant changes that belong to another product section on the page
    const section = this.closest('.shopify-section');
    if (data.sectionId && section && section.id && !section.id.endsWith(data.sectionId)) return;

    const source = data.html.querySelector('product-subscriptions');
    if (!source) return;

    const previous = this.getCurrentSellingPlanId();
    this.innerHTML = source.innerHTML;

    if (previous) {
      const radio = Array.from(this.querySelectorAll('input[name="purchase_option"]')).find((item) => item.value === previous);
      const select = Array.from(this.querySelectorAll('select[name="purchase_option_values"]')).find((item) =>
        Array.from(item.options).some((option) => option.value === previous)
      );
      if (radio) {
        radio.checked = true;
      } else if (select) {
        select.value = previous;
        const rowRadio = this.rowOf(select).querySelector('input[name="purchase_option"]');
        if (rowRadio) rowRadio.checked = true;
      }
      if (radio || select) return this.setPlan(previous);
    }
    this.sync();
  }

  getCurrentSellingPlanId() {
    const input = this.input;
    const value = input ? input.value : '';
    return value === 'onetime' ? '' : value;
  }
}

if (!customElements.get('product-subscriptions')) {
  customElements.define('product-subscriptions', ProductSubscriptions);
}

window.ProductSubscriptions = ProductSubscriptions;

window.getCurrentSellingPlanId = function () {
  const productSubscriptions = document.querySelector('product-subscriptions');
  return productSubscriptions && typeof productSubscriptions.getCurrentSellingPlanId === 'function'
    ? productSubscriptions.getCurrentSellingPlanId()
    : '';
};
